/**
 * Student profile — UX/UI Specification §9.6 and §9.7.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDateShort, formatValue } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { GENDER_LABELS, type StudentOverview } from '../api/types.js';
import { StudentNameDialog } from '../components/StudentNameDialog.js';
import { Button } from '../components/ui/Button.js';
import { useToast } from '../components/ui/ToastContext.js';
import { useApiData } from '../hooks/useApiData.js';
import { AssessmentTab } from './AssessmentTab.js';
import { GoalsTab } from './GoalsTab.js';
import { MeasurementsTab } from './MeasurementsTab.js';
import { OverviewTab } from './OverviewTab.js';
import { PhotosTab } from './PhotosTab.js';
import { PostureTab } from './PostureTab.js';
import { StudentReportsTab } from './StudentReportsTab.js';
import './StudentProfile.css';

interface Tab {
  id:
    | 'overview'
    | 'measurements'
    | 'photos'
    | 'posture'
    | 'goals'
    | 'assessment'
    | 'reports';
  label: string;
}

const TABS: readonly Tab[] = [
  { id: 'overview', label: 'Genel Bakış' },
  { id: 'measurements', label: 'Ölçümler' },
  { id: 'photos', label: 'Fotoğraflar' },
  { id: 'posture', label: 'Postür Analizi' },
  { id: 'goals', label: 'Hedefler' },
  { id: 'assessment', label: 'Değerlendirme' },
  { id: 'reports', label: 'Raporlar' },
];

type TabId = Tab['id'];

export function StudentProfile() {
  const { studentId = '' } = useParams();
  const [tab, setTab] = useState<TabId>('overview');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const toast = useToast();

  const {
    data: student,
    error,
    loading,
    reload,
  } = useApiData<StudentOverview>(studentId ? `/api/students/${studentId}` : null);

  /**
   * Archiving is offered with an undo rather than a confirmation: nothing is
   * lost, the student keeps every measurement, photo and report, and they can
   * be restored in one click.
   */
  async function toggleArchive() {
    if (!student) return;
    setBusy(true);
    setActionError(undefined);

    const archiving = student.status === 'active';
    try {
      await api.post(`/api/students/${student.id}/${archiving ? 'archive' : 'restore'}`);
      reload();

      if (archiving) {
        toast.show(
          `${student.firstName} ${student.lastName} arşivlendi. Ölçümleri ve fotoğrafları duruyor.`,
          {
            label: 'Geri al',
            onAction: async () => {
              await api.post(`/api/students/${student.id}/restore`);
              reload();
            },
          },
        );
      }
    } catch (err) {
      setActionError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="profile__muted">Yükleniyor…</p>;

  if (error || !student) {
    return (
      <section>
        <p role="alert">{error ?? messageFor('NOT_FOUND')}</p>
        <Link to="/ogrenciler" className="btn btn--secondary">
          Öğrenci listesine dön
        </Link>
      </section>
    );
  }

  const identityChips = [
    student.age !== null ? `${student.age} yaş` : null,
    GENDER_LABELS[student.gender],
    formatValue(student.heightCm, 'cm', 1),
    formatValue(student.currentWeightKg, 'kg', 1),
    student.bmi !== null ? `BMI ${formatValue(student.bmi, '', 1)}` : null,
  ].filter((chip): chip is string => chip !== null);

  const initial = student.firstName.charAt(0).toLocaleUpperCase('tr');

  return (
    <section className="profile">
      <p className="profile__breadcrumb">
        <Link to="/ogrenciler">Öğrenciler</Link>
        <span aria-hidden="true"> › </span>
        <span>
          {student.firstName} {student.lastName}
        </span>
      </p>

      <header className="profile__head">
        <div className="profile__identity-row">
          <span className="profile__avatar" aria-hidden="true">
            {initial}
          </span>

          <div>
            <div className="profile__name-row">
              <h1>
                {student.firstName} {student.lastName}
              </h1>
              <button
                type="button"
                className="profile__edit-name"
                onClick={() => setEditing(true)}
                aria-label="İsmi düzenle"
                title="İsmi düzenle"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z" />
                  <path d="m14.5 7.5 3 3" />
                </svg>
              </button>
              <span
                className={
                  student.status === 'active'
                    ? 'profile__status-pill'
                    : 'profile__status-pill profile__status-pill--archived'
                }
              >
                {student.status === 'active' ? 'Aktif Öğrenci' : 'Arşivde'}
              </span>
            </div>

            <div className="profile__chips">
              {identityChips.map((chip) => (
                <span className="profile__chip" key={chip}>
                  {chip}
                </span>
              ))}
              <span className="profile__chip">
                {student.lastMeasurementDate
                  ? `Son ölçüm ${formatDateShort(student.lastMeasurementDate)}`
                  : 'Henüz ölçüm yok'}
              </span>
            </div>
          </div>
        </div>

        <div className="profile__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void toggleArchive()}
            loading={busy}
          >
            {student.status === 'active' ? 'Arşivle' : 'Geri Yükle'}
          </Button>
        </div>
      </header>

      {editing && (
        <StudentNameDialog
          student={student}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
            toast.show('Öğrenci bilgileri güncellendi.');
          }}
        />
      )}

      {actionError && (
        <p className="profile__error" role="alert">
          {actionError}
        </p>
      )}

      <div className="profile__tabs" role="tablist" aria-label="Öğrenci bölümleri">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`panel-${t.id}`}
            className={tab === t.id ? 'profile__tab profile__tab--on' : 'profile__tab'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        className="profile__panel"
        role="tabpanel"
        id={`panel-${tab}`}
        aria-labelledby={`tab-${tab}`}
      >
        {tab === 'overview' && (
          <OverviewTab
            studentId={student.id}
            heightCm={student.heightCm}
            metrics={student.metrics}
            onAddMeasurement={() => setTab('measurements')}
          />
        )}

        {tab === 'measurements' && (
          <MeasurementsTab
            studentId={student.id}
            heightCm={student.heightCm}
            onChanged={reload}
          />
        )}

        {tab === 'photos' && <PhotosTab studentId={student.id} />}

        {tab === 'posture' && <PostureTab studentId={student.id} />}

        {tab === 'goals' && <GoalsTab studentId={student.id} />}

        {tab === 'assessment' && <AssessmentTab studentId={student.id} />}

        {tab === 'reports' && <StudentReportsTab studentId={student.id} />}
      </div>
    </section>
  );
}
